import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Navbar from "../Shared/Feature/Navbar/Navbar";
import SideBar from "../Shared/Feature/Sidebar/SideBar";
import { Box, useTheme, useMediaQuery, Drawer } from "@mui/material";
import axios from "axios";
import ipconfig from "../Core/ipconfig";
import { encryptData, decryptData } from "../Core/util/obfuscate";

export default function RootPage() {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", String(next));
      return next;
    });
  };

  const updateToken = () => {
    const newToken = localStorage.getItem("token");
    setToken(newToken);
  };

  useEffect(() => {
    updateToken();
    const intervalId = setInterval(updateToken, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate("/login");
    } else {
      // Always fetch fresh profile/permissions on mount (hard reload)
      // Cached data is used immediately for rendering, but we refresh in background
      const fetchAdminMe = async () => {
        try {
          const res = await axios.get(`${ipconfig}/adminpanel/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (res.data?.status) {
            const freshData = res.data.data;
            const cachedRaw = localStorage.getItem("adminMe");
            const cachedData = cachedRaw ? decryptData(cachedRaw) : null;

            // Compare fresh permissions with cached
            const freshPerms = JSON.stringify(freshData?.permissions || freshData?.permission || {});
            const cachedPerms = JSON.stringify(cachedData?.permissions || cachedData?.permission || {});

            // Always update the cache with fresh data
            localStorage.setItem("adminMe", encryptData(freshData));

            // If permissions changed and we had old cached data, reload to apply new permissions
            if (cachedData && freshPerms !== cachedPerms) {
              window.location.reload();
            }
          }
        } catch (error) {
          console.error("Failed to fetch admin profile info:", error);
        }
      };
      fetchAdminMe();
    }
  }, [navigate, token]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        backgroundColor: "background.default",
      }}
    >
      {/* Persistent Sidebar on Desktop */}
      {!isMobile && (
        <SideBar 
          isCollapsed={isSidebarCollapsed} 
          onToggleCollapse={handleToggleSidebar} 
        />
      )}

      {/* Slide Drawer Sidebar on Mobile */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          PaperProps={{
            sx: {
              width: "280px",
              backgroundColor: "background.paper",
            }
          }}
        >
          <SideBar 
            isCollapsed={false} 
            onToggleCollapse={() => setIsSidebarOpen(false)} 
          />
        </Drawer>
      )}

      {/* Main content vertical container */}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Navbar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#0f172a",
            padding: "4px",
            boxSizing: "border-box",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
